// @owners { team: patient-fulfillment }
import { type BaseIconName } from '@alto/design-library-tokens';
import { PresentationListItem } from '@alto/design-system';
import React from 'react';
import { isMail } from '~shared/helpers/order';
import { type ShipmentMethod, type ShipmentStatus } from '~shared/types';

type Props = {
  readonly method?: ShipmentMethod;
  readonly status?: ShipmentStatus;
};

const getStatusText = (status?: ShipmentStatus, method?: ShipmentMethod): string => {
  switch (status) {
    case 'delivered':
      return 'Delivered via';
    case 'failed':
      return 'Failed delivery';
    default:
      return isMail(method) ? 'Ships via' : 'Arrives via';
  }
};

const getIcon = ({ method, status }: Props) => {
  if (status === 'delivered') {
    return 'checkmark';
  }
  if (status === 'failed') {
    return 'cancel';
  }
  if (method === 'pickup') {
    return 'handbox';
  }
  if (method?.startsWith('fedex') || method?.startsWith('usps')) {
    return 'box';
  }
  return 'car';
};

const getMethodText = (method?: ShipmentMethod) => {
  if (method?.startsWith('fedex')) {
    return 'FedEx';
  }
  if (method?.startsWith('usps')) {
    return 'USPS';
  }
  if (method === 'pickup') {
    return 'Pick up';
  }
  return 'courier';
};

export const getDeliveryMethod = ({ method, status }: { method?: ShipmentMethod; status?: ShipmentStatus }) => {
  const methodText = getMethodText(method);
  const statusText = getStatusText(status, method);
  return method === 'pickup' ? methodText : `${statusText} ${methodText}`;
};

export const DeliveryCardDeliveryMethodRow = ({ method, status }: Props) => {
  const icon: BaseIconName = getIcon({ method, status });
  return (
    <PresentationListItem
      iconName={icon}
      text={getDeliveryMethod({ method, status })}
    />
  );
};
